import type { PlannerEntryLocation } from './entry-location';
import { AiPlannerAnalyticsActionTypes } from './action-types';
import type { PLANNER_ANALYTICS_SOURCE } from '@bridebook/web/lib/ai-planner/client-actions/agent-dispatch';

// What the conversational onboarding reports when it starts or completes.
interface OnboardingPropsCommon {
  location: typeof PLANNER_ANALYTICS_SOURCE;
  section: 'onboarding';
}

// The conversational onboarding started.
interface StartedOnboardingProps extends OnboardingPropsCommon {}

// The conversational onboarding reached the end of standard onboarding.
interface CompletedOnboardingProps extends OnboardingPropsCommon {
  onboardingCompleted: true;
}

// What every click on a planner control reports. Each kind narrows it to its own values.
interface InteractedWithPlannerPropsCommon {
  action: 'clicked';
  location: PlannerEntryLocation | typeof PLANNER_ANALYTICS_SOURCE;
  section: 'PageSection' | 'closeButton' | typeof PLANNER_ANALYTICS_SOURCE | 'onboarding';
  element: 'icon' | 'toggle' | 'closeButton' | 'escapeKey';
  elementLabel: null | 'Explore Bridebook first';
}

// The couple left the conversational onboarding early with the explore button.
interface SkippedOnboardingProps extends InteractedWithPlannerPropsCommon {
  location: typeof PLANNER_ANALYTICS_SOURCE;
  section: 'onboarding';
  element: 'closeButton';
  elementLabel: 'Explore Bridebook first';
  onboardingCompleted: false;
}

// The couple opened the planner from an entry point.
interface ClickedEntryPointProps extends InteractedWithPlannerPropsCommon {
  location: PlannerEntryLocation;
  section: 'PageSection';
  element: 'icon';
  elementLabel: null;
}

// The couple closed the planner with its close button or the Escape key.
interface ClosedPlannerProps extends InteractedWithPlannerPropsCommon {
  location: typeof PLANNER_ANALYTICS_SOURCE;
  section: 'closeButton';
  element: 'closeButton' | 'escapeKey';
  elementLabel: null;
}

// The couple turned the planner on or off in settings.
interface ToggledSettingProps extends InteractedWithPlannerPropsCommon {
  location: 'settings';
  section: typeof PLANNER_ANALYTICS_SOURCE;
  element: 'toggle';
  elementLabel: null;
  weddingPlannerEnabled: boolean;
}

/**
 * One interaction with the planner. Each kind has its own shape, so every property it
 * needs is required.
 */
export type IInteractedWithPlannerProps =
  | StartedOnboardingProps
  | CompletedOnboardingProps
  | SkippedOnboardingProps
  | ClickedEntryPointProps
  | ClosedPlannerProps
  | ToggledSettingProps;

export const interactedWithPlannerAnalytics = (payload: IInteractedWithPlannerProps) => ({
  type: AiPlannerAnalyticsActionTypes.INTERACTED_WITH_PLANNER,
  payload,
});
